import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Container,
  Backdrop,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { Controller, useForm } from "react-hook-form";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import CustomSnackbar from "./CustomSnackbar";
import { getAllSettings, verifyDbConnection } from "../utils/api";
import { fetchSingleOrgVariable, setOrgVariable } from "../utils/zoho";
import { ORG_VARS } from "../utils/orgVariables";
import {
  buildMysqlConnectionUrl,
  parseMysqlConnectionUrl,
} from "../utils/connectionString";
import {
  BORDER_LIGHT,
  BRAND_COLOR,
  BRAND_COLOR_DARK,
  BRAND_COLOR_TINT_SOFT,
  BUTTON_SHADOW,
  INFO_PANEL_BG,
  TEXT_FAINT,
  TEXT_ON_DARK,
} from "../utils/colors";

const DBConnection = ({
  setTabNumber,
  setDisabledTabs,
  orgId,
  apiKey,
  handleConnectionUrl,
}) => {
  const [showBackdrop, setShowBackDrop] = useState(false);
  const [validConnectionUrl, setValidConnectionUrl] = useState(false);
  const [loader, setLoader] = useState({
    test_conn: false,
    save_conn: false,
  });

  //snackbar state and functions
  const [openSnack, setOpenSnack] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState(""); // success or error
  const [settings, setSettings] = useState(null);

  // a connection that is persisted (org variable) AND verified
  const [savedConnection, setSavedConnection] = useState(false);

  // disconnect confirmation
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  // ConnectionSchema is a legacy backend row, not a configured module
  const moduleSettingsCount = (settings || []).filter(
    (row) => row.module_name !== "ConnectionSchema"
  ).length;

  // Lock editing only while a VERIFIED saved connection is protected by
  // configured modules. A saved URL that fails verification must stay
  // editable so the client can fix it.
  const fieldsLocked = savedConnection && moduleSettingsCount > 0;

  const auth = { orgId, apiKey };

  const handleClickSnack = () => {
    setOpenSnack(true);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const showSnack = (snackMessage, snackSeverity) => {
    setMessage(snackMessage);
    setSeverity(snackSeverity);
    handleClickSnack();
  };

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      port: 3306,
    },
  });

  /** Rebuild the Connection_Url field from the individual part fields. */
  const syncConnectionUrlFromParts = (overrides = {}) => {
    const parts = {
      username: getValues("username"),
      password: getValues("password"),
      hostname: getValues("hostname"),
      port: getValues("port"),
      database: getValues("database"),
      sslOptions: getValues("sslOptions"),
      ...overrides,
    };

    setValue("Connection_Url", buildMysqlConnectionUrl(parts));
  };

  /** Fill the part fields from a pasted/loaded connection URL. */
  const applyParsedConnectionUrl = (connectionUrl, { includePort = true } = {}) => {
    const parsed = parseMysqlConnectionUrl(connectionUrl);

    if (parsed) {
      if (includePort) {
        setValue("port", parsed.port);
      }
      setValue("username", parsed.username);
      setValue("password", parsed.password);
      setValue("hostname", parsed.hostname);
      setValue("database", parsed.database);
      setValue("sslOptions", parsed.sslOptions);
    } else {
      setValue("username", null);
      setValue("password", null);
      setValue("hostname", null);
      setValue("database", null);
      setValue("sslOptions", null);
    }
  };

  /** onChange handler shared by every part field (username, password, host...). */
  const handlePartChange = (field, partName) => (e) => {
    field.onChange(e.target.value);
    setValidConnectionUrl(false);
    setSavedConnection(false); // editing means it is no longer the saved connection
    syncConnectionUrlFromParts({ [partName]: e.target.value });
  };

  useEffect(() => {
    (async () => {
      const connectionUrl = await fetchSingleOrgVariable(
        ORG_VARS.DB_CONNECTION_URL
      );

      if (!connectionUrl || connectionUrl === "null") {
        return;
      }

      setShowBackDrop(true);

      try {
        const allSettings = await getAllSettings(auth);
        setSettings(allSettings);

        const connectionOk = await verifyDbConnection(auth, connectionUrl);

        if (connectionOk) {
          showSnack("Connection Url tested successfully!", "success");
          setValidConnectionUrl(true);
          setSavedConnection(true);
          handleConnectionUrl(connectionUrl);
        } else {
          setValidConnectionUrl(false);
          handleConnectionUrl(null);
        }
      } catch (err) {
        showSnack(`${err.message}. Connection URL is not correct.`, "error");
      } finally {
        setValue("Connection_Url", connectionUrl);
        applyParsedConnectionUrl(connectionUrl, { includePort: false });
        setShowBackDrop(false);
      }
    })();
  }, []);

  const handleTestConnection = async () => {
    try {
      const connectionValue = getValues("Connection_Url");
      setLoader((prev) => ({ ...prev, test_conn: true }));

      const connectionOk = await verifyDbConnection(auth, connectionValue);

      if (connectionOk) {
        clearErrors("Connection_Url");
        setValidConnectionUrl(true);
        showSnack("Connection Url tested successfully!", "success");
      }
    } catch (err) {
      setValidConnectionUrl(false);
      setError("Connection_Url", {
        type: "custom",
        message: "Connection Url is wrong!",
      });
      handleConnectionUrl(null);
      showSnack("Connection Url is wrong!", "error");
    } finally {
      setLoader((prev) => ({ ...prev, test_conn: false }));
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnectLoading(true);

      // forget the saved connection ("null" = unset by convention)
      await setOrgVariable(ORG_VARS.DB_CONNECTION_URL, "null");

      // clear the form and local state so a new connection can be entered
      ["Connection_Url", "username", "password", "hostname", "database", "sslOptions"].forEach(
        (fieldName) => setValue(fieldName, "")
      );
      setValue("port", 3306);
      setValidConnectionUrl(false);
      setSavedConnection(false);
      setSettings(null);
      handleConnectionUrl(null);
      setDisabledTabs((prev) => ({
        ...prev,
        settingsTab: true,
        backupData: true,
        historical: true,
      }));

      showSnack("Database disconnected.", "success");
      setDisconnectOpen(false);
    } catch (err) {
      showSnack("Could not disconnect. Please try again.", "error");
    } finally {
      setDisconnectLoading(false);
    }
  };

  const onsubmit = async (data) => {
    try {
      setLoader((prev) => ({ ...prev, save_conn: true }));

      // Save tests the connection itself — no separate Test click needed.
      // Saving only proceeds when verification succeeds.
      const result = await verifyDbConnection(auth, data.Connection_Url);
      const connectionOk = result === true || result?.data === true;
      if (!connectionOk) {
        throw new Error("Connection verification failed");
      }

      setValidConnectionUrl(true);
      showSnack("Connection verified & saved!", "success");
      handleConnectionUrl(data.Connection_Url);

      // persist the db url as an org variable
      await setOrgVariable(ORG_VARS.DB_CONNECTION_URL, data.Connection_Url);
      setSavedConnection(true);

      clearErrors("Connection_Url");

      setDisabledTabs((prev) => {
        return {
          ...prev,
          settingsTab: false,
        };
      });

      setTimeout(() => {
        handleCloseSnack();
        setTabNumber(2);
      }, 700);
    } catch (err) {
      setValidConnectionUrl(false);
      setError("Connection_Url", {
        type: "custom",
        message: "Something Went Wrong",
      });
      showSnack("Connection Url is wrong!", "error");
      handleConnectionUrl(null);
    } finally {
      setLoader((prev) => ({ ...prev, save_conn: false }));
      setOpenSnack(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Backdrop
        sx={{ color: TEXT_ON_DARK, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "start",
          alignItems: "start",
        }}
      >
        <Box sx={{ width: "60%", height: "100%" }}>
          <Typography sx={{ fontSize: "24px", p: "22px 0 0 40px", mb: "1rem" }}>
            DB Connection
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit(onsubmit)}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "start",
              flexDirection: "column",
              alignItems: "start",
              p: "0 40px 1rem",
            }}
          >
            {/* connection url — quickest way to connect, shown first */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "8px 16px",
              }}
            >
              <Typography sx={{ width: "30%", mt: "-1rem" }}>
                Connection Url
              </Typography>

              <Controller
                control={control}
                name="Connection_Url"
                disabled={fieldsLocked}
                render={({ field, fieldState }) => (
                  <>
                    <TextField
                      size="small"
                      id="connection_url"
                      variant="outlined"
                      value={field.value}
                      fullWidth
                      sx={{ mb: "1rem" }}
                      error={errors["Connection_Url"]}
                      helperText={errors?.["Connection_Url"]?.message}
                      disabled={fieldsLocked}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setValidConnectionUrl(false);
                        setSavedConnection(false); // editing = no longer the saved connection
                        applyParsedConnectionUrl(e.target.value);
                      }}
                    />
                  </>
                )}
              />
            </Box>

            {/* separator between url entry and manual credentials */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                mb: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, borderBottom: `1px solid ${BORDER_LIGHT}` }} />
              <Typography sx={{ fontSize: 13, color: TEXT_FAINT }}>
                or enter credentials manually
              </Typography>
              <Box sx={{ flexGrow: 1, borderBottom: `1px solid ${BORDER_LIGHT}` }} />
            </Box>

            {/* username */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "8px 16px",
              }}
            >
              <Typography sx={{ width: "30%", mt: "-1rem" }}>
                UserName
              </Typography>

              <Controller
                control={control}
                name="username"
                rules={{ required: true }}
                disabled={fieldsLocked}
                render={({ field, fieldState }) => (
                  <TextField
                    error={!!fieldState.error}
                    helperText={fieldState?.error?.message}
                    size="small"
                    id="username"
                    variant="outlined"
                    fullWidth
                    {...field}
                    sx={{ mb: "1rem" }}
                    onChange={handlePartChange(field, "username")}
                  />
                )}
              />
            </Box>
            {/* password */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "8px 16px",
              }}
            >
              <Typography sx={{ width: "30%", mt: "-1rem" }}>
                Password
              </Typography>

              <Controller
                control={control}
                name="password"
                rules={{ required: true }}
                disabled={fieldsLocked}
                render={({ field, fieldState }) => (
                  <TextField
                    error={!!fieldState.error}
                    helperText={fieldState?.error?.message}
                    size="small"
                    id="password"
                    variant="outlined"
                    fullWidth
                    {...field}
                    sx={{ mb: "1rem" }}
                    type="password"
                    onChange={handlePartChange(field, "password")}
                  />
                )}
              />
            </Box>

            {/* host and port */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "8px 16px",
                mt: "16px",
                mb: "16px",
              }}
            >
              <Typography sx={{ width: "30%", mt: "-1rem" }}>Host</Typography>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Controller
                  control={control}
                  name="hostname"
                  disabled={fieldsLocked}
                  render={({ field, fieldState }) => (
                    <TextField
                      size="small"
                      id="hostname"
                      variant="outlined"
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message}
                      {...field}
                      sx={{ width: "55%" }}
                      onChange={handlePartChange(field, "hostname")}
                    />
                  )}
                />

                <Typography sx={{ ml: "1rem" }}>Port</Typography>

                <Controller
                  control={control}
                  name="port"
                  disabled={fieldsLocked}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      id="port"
                      variant="outlined"
                      {...field}
                      sx={{ width: "32%" }}
                      onChange={handlePartChange(field, "port")}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* database */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "8px 16px",
              }}
            >
              <Typography sx={{ width: "30%", mt: "-1rem" }}>
                Database
              </Typography>

              <Controller
                control={control}
                name="database"
                disabled={fieldsLocked}
                rules={{ required: "This is required Field" }}
                render={({ field, fieldState }) => (
                  <>
                    <TextField
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message}
                      size="small"
                      id="database"
                      variant="outlined"
                      fullWidth
                      {...field}
                      sx={{ mb: "1rem" }}
                      onChange={handlePartChange(field, "database")}
                    />
                  </>
                )}
              />
            </Box>

            {/* ssl options */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "8px 16px",
              }}
            >
              <Typography sx={{ width: "30%", mt: "-1rem" }}>
                SSl options
              </Typography>

              <Controller
                control={control}
                name="sslOptions"
                disabled={fieldsLocked}
                render={({ field }) => (
                  <TextField
                    size="small"
                    id="sslOptions"
                    variant="outlined"
                    fullWidth
                    {...field}
                    sx={{ mb: "1rem" }}
                    onChange={handlePartChange(field, "sslOptions")}
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                p: "8px 16px",
                gap: "1rem",
              }}
            >
              {savedConnection && (
                <Button
                  variant="outlined"
                  color="error"
                  sx={{ p: "8px 22px" }}
                  onClick={() => setDisconnectOpen(true)}
                >
                  Disconnect
                </Button>
              )}
              <LoadingButton
                loading={loader.test_conn}
                variant="outlined"
                sx={{
                  backgroundColor: "transparent",
                  boxShadow: BUTTON_SHADOW,
                  border: `1px solid ${BRAND_COLOR}`,
                  "&:hover": {
                    backgroundColor: BRAND_COLOR_TINT_SOFT,
                    border: `1px solid ${BRAND_COLOR}`,
                  },
                  color: BRAND_COLOR,
                  p: "8px 22px",
                }}
                onClick={handleTestConnection}
              >
                Test Connection
              </LoadingButton>

              {!savedConnection && (
                <LoadingButton
                  type="submit"
                  loading={loader.save_conn}
                  variant="contained"
                  disabled={fieldsLocked}
                  sx={{
                    backgroundColor: BRAND_COLOR,
                    border: `1px solid ${BRAND_COLOR}`,
                    boxShadow: BUTTON_SHADOW,
                    "&:hover": {
                      backgroundColor: BRAND_COLOR_DARK,
                    },
                    p: "8px 22px",
                  }}
                >
                  Save Connection
                </LoadingButton>
              )}
            </Box>

            <CustomSnackbar
              open={openSnack}
              handleClose={handleCloseSnack}
              message={message}
              severity={severity}
            />

            {/* disconnect re-confirmation */}
            <Dialog
              open={disconnectOpen}
              onClose={() => !disconnectLoading && setDisconnectOpen(false)}
            >
              <DialogTitle>Disconnect database?</DialogTitle>
              <DialogContent sx={{ maxWidth: 440 }}>
                {moduleSettingsCount > 0 ? (
                  <Typography sx={{ fontSize: 14.5 }}>
                    You still have {moduleSettingsCount} module
                    {moduleSettingsCount > 1 ? "s" : ""} configured. Delete all
                    modules from the <strong>SETTINGS</strong> tab first, then
                    disconnect the database.
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 14.5 }}>
                    This removes the saved connection URL. Sync, backup and
                    historical data will stop working until you connect a
                    database again. Data already stored in your database is
                    not deleted.
                  </Typography>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  variant="outlined"
                  disabled={disconnectLoading}
                  onClick={() => setDisconnectOpen(false)}
                >
                  {moduleSettingsCount > 0 ? "Close" : "Cancel"}
                </Button>
                {moduleSettingsCount === 0 && (
                  <LoadingButton
                    loading={disconnectLoading}
                    variant="contained"
                    color="error"
                    onClick={handleDisconnect}
                  >
                    Disconnect
                  </LoadingButton>
                )}
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
        <Box
          sx={{
            width: "40%",
            height: "100%",
            backgroundColor: INFO_PANEL_BG,
            px: 2,
            py: 4.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              mb: "1rem",
            }}
          >
            To facilitate a secure and efficient connection to your database, we
            require certain essential credentials from your end. Please provide
            the following information at your earliest convenience:
          </Typography>

          <Typography>
            <span style={{ fontWeight: "bold" }}> Host Name:</span> The name or
            address of your database server.
          </Typography>
          <Typography>
            <span style={{ fontWeight: "bold" }}>Port:</span> The port number
            (Default port for MySql is 3306)
          </Typography>
          <Typography>
            <span style={{ fontWeight: "bold" }}>Username:</span> The username
            for accessing the database.
          </Typography>
          <Typography>
            <span style={{ fontWeight: "bold" }}>Password:</span> The password
            associated with your username.
          </Typography>
          <Typography>
            <span style={{ fontWeight: "bold" }}>Database Name:</span> The
            specific name of the database you wish to access.
          </Typography>
          <Typography>OR</Typography>
          <Typography>
            <span style={{ fontWeight: "bold" }}>Connection URL:</span> The URL
            used for establishing a connection to your database.
          </Typography>
          <Typography sx={{ mt: 3 }}>
            For enhanced security and to ensure uninterrupted connectivity, it's
            crucial to whitelist
            <span style={{ fontWeight: "bold", fontSize: "18px" }}>
              {" "}
              IP address %%%%
            </span>{" "}
            in your database firewall settings / Remote MySQL in CPanel. This is
            a vital step for a secure and reliable connection.
          </Typography>
          <Box mt={2}>
            <Alert severity="warning">
              If you want to change the <strong>Connection Url</strong>, you
              must need to delete all the modules from the{" "}
              <strong>SETTINGS</strong> tab.
            </Alert>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default DBConnection;
